import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BattleStateView, Direction, GameAction, MapDocument, Side } from '@tank-battle/shared';
import { getFogAt } from '@tank-battle/shared';
import { WS_BATTLE_URL, getTelegramInitData } from '../config.js';
import { createBattle, fetchMap, getMatchmakingStatus, joinBattle, joinMatchmaking, leaveMatchmaking } from './api.js';
import { canFireAtCell, readBattleIdFromLaunch } from './actionHelpers.js';
import type { ActionMode } from './actionHelpers.js';
import type { ClientMessage, ServerMessage } from './protocol.js';

export interface BattleSessionState {
  phase: 'lobby' | 'matching' | 'connecting' | 'waiting' | 'battle' | 'ended';
  battleId: string | null;
  side: Side | null;
  view: BattleStateView | null;
  map: MapDocument | null;
  turnDeadline: number | null;
  apRemaining: number | null;
  lastAction: GameAction | null;
  error: string | null;
  status: string | null;
  winner: Side | null;
  skippedTurns: Partial<Record<Side, number>>;
  secondsLeft: number;
  opponentJoined: boolean;
  queueSize: number;
}

const initialState: BattleSessionState = {
  phase: 'lobby',
  battleId: null,
  side: null,
  view: null,
  map: null,
  turnDeadline: null,
  apRemaining: null,
  lastAction: null,
  error: null,
  status: null,
  winner: null,
  skippedTurns: {},
  secondsLeft: 0,
  opponentJoined: false,
  queueSize: 0,
};

function parseServerMessage(raw: string): ServerMessage | null {
  try {
    return JSON.parse(raw) as ServerMessage;
  } catch {
    return null;
  }
}

export function useBattleSession() {
  const [state, setState] = useState<BattleSessionState>(initialState);
  const [actionMode, setActionMode] = useState<ActionMode>('move');
  const wsRef = useRef<WebSocket | null>(null);
  const battleIdRef = useRef<string | null>(null);
  const sideRef = useRef<Side | null>(null);
  const matchPollRef = useRef<number | null>(null);

  const closeWs = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
  }, []);

  const stopMatchPoll = useCallback(() => {
    if (matchPollRef.current !== null) {
      window.clearInterval(matchPollRef.current);
      matchPollRef.current = null;
    }
  }, []);

  const sendMessage = useCallback((message: ClientMessage) => {
    const initData = getTelegramInitData();
    wsRef.current?.send(JSON.stringify({ ...message, initData: initData || undefined }));
  }, []);

  const connectWs = useCallback(
    (battleId: string, side: Side) => {
      closeWs();
      setState((prev) => ({
        ...prev,
        phase: side === 'A' ? 'waiting' : 'connecting',
        error: null,
        status: side === 'A' ? 'Waiting for opponent…' : 'Connecting WS…',
        opponentJoined: side === 'B',
      }));
      battleIdRef.current = battleId;
      sideRef.current = side;

      const ws = new WebSocket(WS_BATTLE_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        sendMessage({ t: 'JOIN', battleId, side });
      };

      ws.onmessage = (event) => {
        const message = parseServerMessage(String(event.data));
        if (!message) return;

        if (message.t === 'JOINED' || message.t === 'SNAPSHOT') {
          setState((prev) => ({
            ...prev,
            phase:
              message.view.phase === 'ended'
                ? 'ended'
                : prev.opponentJoined || sideRef.current === 'B'
                  ? 'battle'
                  : 'waiting',
            view: message.view,
            turnDeadline: message.turnDeadline,
            apRemaining:
              'apRemaining' in message
                ? (message.apRemaining ?? prev.apRemaining)
                : prev.apRemaining,
            lastAction:
              message.t === 'SNAPSHOT'
                ? (message.lastAction ?? null)
                : null,
            winner: message.view.winner ?? prev.winner,
            error: null,
            status:
              message.t === 'SNAPSHOT' && message.timedOut
                ? 'Turn skipped (timeout)'
                : prev.opponentJoined || sideRef.current === 'B'
                  ? 'Connected'
                  : 'Waiting for opponent…',
          }));
          return;
        }

        if (message.t === 'TURN_TIMEOUT') {
          setState((prev) => ({
            ...prev,
            status: `Side ${message.side} timed out (${message.skippedTurns} skips)`,
            skippedTurns: { ...prev.skippedTurns, [message.side]: message.skippedTurns },
          }));
          return;
        }

        if (message.t === 'BATTLE_END') {
          setState((prev) => ({
            ...prev,
            phase: 'ended',
            winner: message.winner,
            status: `Battle ended — winner: ${message.winner}`,
          }));
          return;
        }

        if (message.t === 'OPPONENT_JOINED') {
          setState((prev) => ({
            ...prev,
            opponentJoined: true,
            phase: 'battle',
            status: `Opponent joined (${message.side})`,
          }));
          return;
        }

        if (message.t === 'ERROR') {
          setState((prev) => ({
            ...prev,
            error: `${message.code}: ${message.message}`,
          }));
        }
      };

      ws.onclose = () => {
        setState((prev) => ({
          ...prev,
          status: prev.phase === 'ended' ? prev.status : 'Disconnected',
        }));
      };
    },
    [closeWs, sendMessage],
  );

  const enterMatchedBattle = useCallback(
    async (battleId: string, side: Side, mapId: string) => {
      stopMatchPoll();
      const map = await fetchMap(mapId);
      setState((prev) => ({
        ...prev,
        battleId,
        side,
        map,
        opponentJoined: side === 'B',
        queueSize: 0,
        error: null,
      }));
      connectWs(battleId, side);
    },
    [connectWs, stopMatchPoll],
  );

  const handleMatchmakingResponse = useCallback(
    async (response: Awaited<ReturnType<typeof joinMatchmaking>>) => {
      if (response.status === 'matched' && response.battleId && response.side && response.mapId) {
        await enterMatchedBattle(response.battleId, response.side, response.mapId);
        return;
      }

      if (response.status === 'waiting') {
        setState((prev) => ({
          ...prev,
          phase: 'matching',
          status: 'Searching for opponent…',
          queueSize: response.queueSize ?? prev.queueSize,
          error: null,
        }));
      }
    },
    [enterMatchedBattle],
  );

  const findOpponent = useCallback(async () => {
    try {
      stopMatchPoll();
      setState((prev) => ({
        ...prev,
        phase: 'matching',
        error: null,
        status: 'Joining queue…',
        queueSize: 0,
      }));
      const response = await joinMatchmaking();
      await handleMatchmakingResponse(response);

      if (response.status === 'waiting') {
        matchPollRef.current = window.setInterval(() => {
          void getMatchmakingStatus()
            .then((status) => handleMatchmakingResponse(status))
            .catch((err) => {
              setState((prev) => ({
                ...prev,
                error: err instanceof Error ? err.message : 'Matchmaking failed',
              }));
            });
        }, 2000);
      }
    } catch (err) {
      setState((prev) => ({
        ...prev,
        phase: 'lobby',
        error: err instanceof Error ? err.message : 'Matchmaking failed',
      }));
    }
  }, [handleMatchmakingResponse, stopMatchPoll]);

  const cancelMatchmaking = useCallback(async () => {
    stopMatchPoll();
    try {
      await leaveMatchmaking();
    } catch {
      // ignore leave errors when returning to lobby
    }
    setState((prev) => ({
      ...prev,
      phase: 'lobby',
      status: null,
      queueSize: 0,
      error: null,
    }));
  }, [stopMatchPoll]);

  const startAsSideA = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, error: null, status: 'Creating battle…' }));
      const created = await createBattle();
      const map = await fetchMap(created.mapId);
      setState((prev) => ({
        ...prev,
        battleId: created.battleId,
        side: created.side,
        map,
      }));
      connectWs(created.battleId, created.side);
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Create failed',
      }));
    }
  }, [connectWs]);

  const joinAsSideB = useCallback(
    async (battleId: string) => {
      try {
        setState((prev) => ({ ...prev, error: null, status: 'Joining battle…' }));
        const joined = await joinBattle(battleId.trim());
        const map = await fetchMap(joined.mapId);
        setState((prev) => ({
          ...prev,
          battleId: joined.battleId,
          side: joined.side,
          map,
          opponentJoined: true,
        }));
        connectWs(joined.battleId, joined.side);
      } catch (err) {
        setState((prev) => ({
          ...prev,
          error: err instanceof Error ? err.message : 'Join failed',
        }));
      }
    },
    [connectWs],
  );

  const sendAction = useCallback(
    (action: GameAction) => {
      const battleId = battleIdRef.current;
      if (!battleId) return;
      sendMessage({ t: 'ACTION', battleId, action });
    },
    [sendMessage],
  );

  const endTurn = useCallback(() => {
    const unitId = state.view?.activeUnitId;
    if (!unitId) return;
    sendAction({ type: 'END_TURN', unitId });
    setActionMode('move');
  }, [sendAction, state.view?.activeUnitId]);

  const moveTo = useCallback(
    (x: number, y: number) => {
      const unitId = state.view?.activeUnitId;
      if (!unitId) return;
      sendAction({ type: 'MOVE', unitId, target: { x, y } });
    },
    [sendAction, state.view?.activeUnitId],
  );

  const fireAt = useCallback(
    (x: number, y: number) => {
      const unitId = state.view?.activeUnitId;
      if (!unitId) return;
      sendAction({ type: 'FIRE', unitId, target: { x, y } });
    },
    [sendAction, state.view?.activeUnitId],
  );

  const rotateTo = useCallback(
    (kind: 'ROTATE_TURRET' | 'ROTATE_HULL', facing: Direction) => {
      const unitId = state.view?.activeUnitId;
      if (!unitId) return;
      sendAction({ type: kind, unitId, facing });
    },
    [sendAction, state.view?.activeUnitId],
  );

  useEffect(() => {
    return () => {
      closeWs();
      stopMatchPoll();
    };
  }, [closeWs, stopMatchPoll]);

  useEffect(() => {
    if (!state.turnDeadline) {
      setState((prev) => (prev.secondsLeft === 0 ? prev : { ...prev, secondsLeft: 0 }));
      return;
    }

    const tick = () => {
      const secondsLeft = Math.max(0, Math.ceil((state.turnDeadline! - Date.now()) / 1000));
      setState((prev) =>
        prev.secondsLeft === secondsLeft ? prev : { ...prev, secondsLeft },
      );
    };
    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [state.turnDeadline]);

  const activeUnit = useMemo(() => {
    if (!state.view?.activeUnitId) return null;
    return state.view.units.find((u) => u.id === state.view?.activeUnitId) ?? null;
  }, [state.view]);

  const isMyTurn = useMemo(() => {
    if (!state.side || !state.view) return false;
    return (
      state.view.activeSide === state.side &&
      state.view.phase === 'active' &&
      state.opponentJoined
    );
  }, [state.opponentJoined, state.side, state.view]);

  const canMoveTo = useCallback(
    (x: number, y: number) => {
      if (!state.map || !state.view || !activeUnit || !isMyTurn) return false;
      if (getFogAt(state.view.fog, x, y) === 'hidden') return false;
      const cell = state.map.terrain[y * state.map.size.width + x];
      if (!cell?.passable) return false;
      const occupied = state.view.units.some(
        (u) => u.hp > 0 && u.x === x && u.y === y && u.id !== activeUnit.id,
      );
      return !occupied;
    },
    [activeUnit, isMyTurn, state.map, state.view],
  );

  const canFireAt = useCallback(
    (x: number, y: number) => {
      if (!state.view || !activeUnit || !isMyTurn) return false;
      return canFireAtCell(state.view, activeUnit, x, y);
    },
    [activeUnit, isMyTurn, state.view],
  );

  const launchBattleId = useMemo(() => readBattleIdFromLaunch(), []);

  return {
    state,
    activeUnit,
    isMyTurn,
    actionMode,
    setActionMode,
    launchBattleId,
    startAsSideA,
    joinAsSideB,
    findOpponent,
    cancelMatchmaking,
    endTurn,
    moveTo,
    fireAt,
    rotateTo,
    canMoveTo,
    canFireAt,
  };
}
